/**
 * Post-SSO loader deferral. Once the SSO step loader ("Almost ready...") completes, the next page
 * must not flash the bubbles loader straight away: it stays hidden for the first
 * SSO_LOADER_DEFER_MS and only appears if that page is still loading after that.
 *
 * Every SSO success exit is a full page load, so the deadline is handed over through
 * sessionStorage and applied by the inline <head> script in public/index.html (it has to run
 * before the boot loader paints). That script adds html.dc-loader-deferred, which hides
 * .dc-loader-overlay / inline loaders (custom.css), and removes it at the deadline.
 * Keep the key and duration in sync with public/index.html.
 *
 * The same quiet window then applies to page navigation inside the CP intake flow (/service, the
 * Service.js step machine) for the rest of the SSO-entered session in this tab: a step that loads
 * within SSO_LOADER_DEFER_MS shows no loader at all. Every other page, and loaders not caused by a
 * navigation, are untouched: a save/submit loader already on screen is never hidden, any user
 * click, key press or form submit ends the window at once, and a reload is not a navigation. sessionStorage is per tab and cleared on logout, so a native sign-in
 * never sees it.
 */
export const SSO_LOADER_DEFER_KEY = "dc_sso_loader_defer_until";
export const SSO_LOADER_DEFER_MS = 1000;
const SSO_SESSION_KEY = "dc_sso_session";
const DEFERRED_CLASS = "dc-loader-deferred";
const INTAKE_PATH = "/service";

/** Starts the quiet window; call right before the post-SSO redirect. */
export const deferLoaderAfterSso = () => {
    try {
        sessionStorage.setItem(SSO_LOADER_DEFER_KEY, String(Date.now() + SSO_LOADER_DEFER_MS));
        sessionStorage.setItem(SSO_SESSION_KEY, "1");
    } catch (e) {
        // Storage unavailable — the next page simply shows its loader as before.
    }
};

const isSsoSession = () => {
    try {
        return sessionStorage.getItem(SSO_SESSION_KEY) === "1";
    } catch (e) {
        return false;
    }
};

let navDeferTimer = null;

const USER_ACTION_EVENTS = ["pointerdown", "keydown", "submit"];

/** Ends the navigation quiet window now, so a following save/submit loader shows immediately. */
const endNavigationDefer = () => {
    clearTimeout(navDeferTimer);
    navDeferTimer = null;
    document.documentElement.classList.remove(DEFERRED_CLASS);
    USER_ACTION_EVENTS.forEach((type) => document.removeEventListener(type, endNavigationDefer, true));
};

/**
 * Hides the loader for the first SSO_LOADER_DEFER_MS of an intake-flow navigation in an SSO session.
 * loaderVisible: a loader already on screen (a save/submit leading into the navigation) stays as is.
 * A navigation inside the window restarts it; the overlay still blocks clicks while hidden.
 */
export const deferLoaderOnNavigation = (loaderVisible = false) => {
    if (loaderVisible || window.location.pathname.toLowerCase() !== INTAKE_PATH || !isSsoSession()) return;
    clearTimeout(navDeferTimer);
    document.documentElement.classList.add(DEFERRED_CLASS);
    USER_ACTION_EVENTS.forEach((type) => document.addEventListener(type, endNavigationDefer, true));
    navDeferTimer = setTimeout(endNavigationDefer, SSO_LOADER_DEFER_MS);
};
